/**
 * Online Consultation Service
 * Handles all online consultation-related API calls
 */

import axios from '../api/axios';
import { API_ENDPOINTS } from './api.config';
import { handleApiError } from '../utils/errorHandler';

class ConsultationService {
  /**
   * Get list of online doctors
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search query (optional)
   * @param {string} params.specialization - Filter by specialization (optional)
   * @returns {Promise<Object>} Response with doctors array
   */
  async getOnlineDoctors(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.specialization) queryParams.append('specialization', params.specialization);

      const response = await axios.get(
        `${API_ENDPOINTS.CONSULTATIONS.ONLINE_DOCTORS}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Start chat consultation with a doctor
   * @param {number} doctorId - Doctor ID
   * @param {string} initialMessage - Initial message to doctor (optional)
   * @returns {Promise<Object>} Response with consultation details
   */
  async startChatConsultation(doctorId, initialMessage = null) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.CONSULTATIONS.START_CHAT,
        {
          doctor_id: doctorId,
          message: initialMessage,
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get free video-consultation offer info for a date (and optionally a doctor)
   * @param {Object} params
   * @param {number} [params.doctorId] - Doctor ID (optional)
   * @param {string} [params.date] - Date YYYY-MM-DD (optional, defaults to today)
   * @returns {Promise<Object>} { active, title, free_slots_remaining, is_free_available, ... }
   */
  async getVideoOfferInfo(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.doctorId) queryParams.append('doctor_id', params.doctorId);
      if (params.date) queryParams.append('date', params.date);
      if (params.time) queryParams.append('time', params.time);

      const response = await axios.get(
        `${API_ENDPOINTS.CONSULTATIONS.VIDEO_OFFER_INFO}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      // Non-critical (banner only) — fail soft so booking still works.
      return { active: false };
    }
  }

  /**
   * List all currently-active video-consultation offers (for the "current offers" view)
   * @param {Object} params
   * @param {string} [params.date] - Date YYYY-MM-DD (optional, defaults to today)
   * @returns {Promise<Object>} { date, offers: [...] }
   */
  async getActiveVideoOffers(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.date) queryParams.append('date', params.date);

      const response = await axios.get(
        `${API_ENDPOINTS.CONSULTATIONS.ACTIVE_VIDEO_OFFERS}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      return { offers: [] };
    }
  }

  /**
   * Get available video consultation slots for a doctor
   * @param {number} doctorId - Doctor ID
   * @param {string} date - Date in YYYY-MM-DD format (optional)
   * @returns {Promise<Object>} Response with available slots
   */
  async getVideoConsultationSlots(doctorId, date = null) {
    try {
      const queryParams = new URLSearchParams();
      if (date) queryParams.append('date', date);

      const response = await axios.get(
        `${API_ENDPOINTS.CONSULTATIONS.VIDEO_SLOTS(doctorId)}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Book video consultation
   * @param {Object} bookingData - Booking data
   * @param {number} bookingData.doctor_id - Doctor ID (required)
   * @param {string} bookingData.slot_time - ISO datetime "2025-01-20T11:00:00Z" (required)
   * @param {number} bookingData.price - Consultation price (optional)
   * @param {string} bookingData.patient_whatsapp - WhatsApp number (required)
   * @returns {Promise<Object>} Response with consultation details
   */
  async bookVideoConsultation(bookingData) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.CONSULTATIONS.BOOK_VIDEO,
        {
          doctor_id: bookingData.doctor_id,
          slot_time: bookingData.slot_time,
          price: bookingData.price,
          patient_whatsapp: bookingData.patient_whatsapp,
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get consultation messages
   * @param {number} consultationId - Consultation ID
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.page_size - Messages per page (default: 50, max: 100)
   * @returns {Promise<Object>} Response with messages array and pagination
   */
  async getConsultationMessages(consultationId, params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        page_size: params.page_size || 50,
      });

      const response = await axios.get(
        `${API_ENDPOINTS.CONSULTATIONS.MESSAGES(consultationId)}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Send message in consultation
   * @param {number} consultationId - Consultation ID
   * @param {string} message - Message text (required)
   * @returns {Promise<Object>} Response with message details
   */
  async sendMessage(consultationId, message) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.CONSULTATIONS.SEND_MESSAGE(consultationId),
        {
          message: message,
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Check if current time is within chat hours
   * @returns {boolean} Whether chat is available
   */
  isChatAvailable() {
    const now = new Date();
    const hour = now.getHours();
    // Chat available: 9 AM - 10 PM (9-22)
    return hour >= 9 && hour < 22;
  }

  /**
   * Get user consultations
   * @returns {Promise<Object>} Response with consultations array
   */
  async getUserConsultations() {
    try {
      const response = await axios.get(API_ENDPOINTS.CONSULTATIONS.CONSULTATIONS);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get consultation info/details
   * @param {number} consultationId - Consultation ID
   * @returns {Promise<Object>} Response with consultation details
   */
  async getConsultationInfo(consultationId) {
    try {
      const response = await axios.get(
        API_ENDPOINTS.CONSULTATIONS.INFO(consultationId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * End consultation
   * @param {number} consultationId - Consultation ID
   * @returns {Promise<Object>} Response with message
   */
  async endConsultation(consultationId) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.CONSULTATIONS.END(consultationId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Reschedule consultation (video only)
   * @param {number} consultationId - Consultation ID
   * @param {string} newDate - New date YYYY-MM-DD
   * @param {string} newSlotTime - New time HH:MM
   * @returns {Promise<Object>} Response with updated consultation
   */
  async rescheduleConsultation(consultationId, newDate, newSlotTime) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.CONSULTATIONS.RESCHEDULE(consultationId),
        { new_date: newDate, new_slot_time: newSlotTime }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Cancel consultation (note: API policy does not allow cancellation)
   * @param {number} consultationId - Consultation ID
   * @returns {Promise<Object>} Response (will return error per policy)
   */
  async cancelConsultation(consultationId) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.CONSULTATIONS.CANCEL(consultationId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Mark notifications as read
   * @param {number} consultationId - Consultation ID
   * @returns {Promise<Object>} Response with message
   */
  async markNotificationsRead(consultationId) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.CONSULTATIONS.MARK_READ(consultationId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get consultation history
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise<Object>} Response with history and pagination
   */
  async getHistory(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);

      const response = await axios.get(
        `${API_ENDPOINTS.CONSULTATIONS.HISTORY}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Generate invoice for consultation
   * @param {number} consultationId - Consultation ID
   * @returns {Promise<Object>} Response with invoice URL
   */
  async generateInvoice(consultationId) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.CONSULTATIONS.GENERATE_INVOICE(consultationId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Download invoice for consultation
   * @param {number} consultationId - Consultation ID
   * @returns {Promise<Blob>} PDF file blob
   */
  async downloadInvoice(consultationId) {
    try {
      const response = await axios.get(
        API_ENDPOINTS.CONSULTATIONS.DOWNLOAD_INVOICE(consultationId),
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get the doctor-authored prescription for a consultation
   * @param {number} consultationId - Consultation ID
   * @returns {Promise<Object>} Response with prescription details
   */
  async getPrescription(consultationId) {
    try {
      const response = await axios.get(
        API_ENDPOINTS.CONSULTATIONS.PRESCRIPTION(consultationId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Download the prescription PDF for a consultation
   * @param {number} consultationId - Consultation ID
   * @returns {Promise<Blob>} PDF file blob
   */
  async downloadPrescription(consultationId) {
    try {
      const response = await axios.get(
        API_ENDPOINTS.CONSULTATIONS.DOWNLOAD_PRESCRIPTION(consultationId),
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Check if slot is within booking rules for video consultation
   * @param {string} slotDateTime - Slot datetime ISO string
   * @returns {Object} Validation result
   */
  validateVideoSlotTime(slotDateTime) {
    const now = new Date();
    const slotDate = new Date(slotDateTime);
    const diffMs = slotDate - now;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffMs < 0) {
      return {
        valid: false,
        message: 'Selected slot is in the past',
      };
    }

    // Allow booking for any future date (no maximum advance booking limit)
    return {
      valid: true,
      message: 'Slot is available',
    };
  }

  /**
   * Convert time string to ISO datetime
   * @param {string} date - Date string YYYY-MM-DD
   * @param {string} time - Time string "11:00 AM"
   * @returns {string} ISO datetime string
   */
  convertToISODateTime(date, time) {
    const dateTime = new Date(`${date} ${time}`);
    return dateTime.toISOString();
  }
}

export default new ConsultationService();
